// App «Salud · DIF» del SO Orion (Orion for Government).
//   /m/gov-health/panorama       → KPIs, mapa de atención y sugerencias de ruta
//   /m/gov-health/jornada        → jornada en vivo: cada persona → su expediente MedOS
//   /m/gov-health/ruta           → calendario de la Ruta de la Salud
//   /m/gov-health/senales        → señales epidemiológicas + anomalías explicadas
//   /m/gov-health/canalizaciones → canalizaciones (exige permiso clínico)
//   /m/gov-health/asistente      → MedOS + Asistente médico (MedGemma): borrador → revisión → firma
// Nada de aquí diagnostica: cifras poblacionales para que una persona decida.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity, CalendarDays, ClipboardPlus, HeartPulse, MapPin, Route, Send, Sparkles, Stethoscope, Users,
} from 'lucide-react';

import { OsApp, OsTabs, osActionBtn } from '@/modules/orion-shell/OsApp';
import { Card, EmptyState, ErrorNote, Loading, Sparkline } from '@/modules/orion-shell/ui';

import { govHealthApi } from '../api';
import type { Anomalia, Ruta } from '../types';
import { ExpedienteDrawer } from './ExpedienteDrawer';
import { GovMap, type MapPoint } from './GovMap';
import { JornadaView } from './JornadaView';
import { MedAssistant } from './MedAssistant';
import { ComplianceBanner, NIVEL_COLOR, NivelChip, OrigenBadge, Stat, fechaCorta, fmt } from './parts';

export type GovHealthView = 'panorama' | 'jornada' | 'ruta' | 'senales' | 'canalizaciones' | 'asistente';

const TABS: { key: GovHealthView; label: string; icon: any }[] = [
  { key: 'panorama', label: 'Panorama', icon: Activity },
  { key: 'jornada', label: 'Jornada', icon: ClipboardPlus },
  { key: 'ruta', label: 'Ruta', icon: Route },
  { key: 'senales', label: 'Señales', icon: HeartPulse },
  { key: 'canalizaciones', label: 'Canalizaciones', icon: Send },
  { key: 'asistente', label: 'Asistente', icon: Stethoscope },
];

export function GovHealthApp({ view }: { view: GovHealthView }) {
  const navigate = useNavigate();
  const resumen = useQuery({ queryKey: ['gov-health', 'resumen'], queryFn: () => govHealthApi.resumen('mes') });
  const origen = resumen.data?.origen ?? 'sin_datos';
  return (
    <OsApp
      icon={HeartPulse}
      title="Salud · DIF"
      subtitle={<span className="inline-flex items-center gap-2">Ruta de la Salud y vigilancia poblacional <OrigenBadge origen={origen} /></span>}
      tabs={<OsTabs label="Secciones de Salud · DIF" value={view} onChange={(v) => navigate(`/m/gov-health/${v}`)} items={TABS} />}
    >
      <div className="flex flex-col gap-4">
        <ComplianceBanner />
        {resumen.isError && (resumen.error as any)?.status === 404 ? (
          <EmptyState icon={<HeartPulse size={18} />} title="Salud · DIF no está activo para esta organización">
            Lo activa la plataforma (gov_modules_org). No se muestran datos de otra organización.
          </EmptyState>
        ) : view === 'panorama' ? <Panorama />
          : view === 'jornada' ? <JornadaView />
          : view === 'ruta' ? <RutaView />
          : view === 'senales' ? <SenalesView />
          : view === 'canalizaciones' ? <CanalizacionesView />
          : <MedAssistant />}
      </div>
    </OsApp>
  );
}

// ── Panorama ──────────────────────────────────────────────────

function Panorama() {
  const r = useQuery({ queryKey: ['gov-health', 'resumen'], queryFn: () => govHealthApi.resumen('mes') });
  const s = useQuery({ queryKey: ['gov-health', 'senales'], queryFn: () => govHealthApi.senales() });
  const sug = useQuery({ queryKey: ['gov-health', 'sugerir'], queryFn: () => govHealthApi.sugerir() });
  const cal = useQuery({ queryKey: ['gov-health', 'calendario'], queryFn: () => govHealthApi.calendario() });
  const navigate = useNavigate();
  const hoy = useQuery({ queryKey: ['gov-health', 'jornada', ''], queryFn: () => govHealthApi.jornada(), refetchInterval: 15000 });
  const proximas = useMemo(() => (cal.data?.rutas ?? []).filter((x) => x.estado === 'programada').slice(0, 4), [cal.data]);
  const points: MapPoint[] = useMemo(() => (r.data?.por_colonia ?? []).map((c) => ({
    lat: c.lat, lon: c.lon, label: c.colonia, color: '#8B5CF6',
    radius: 6 + Math.min(16, c.atendidos / 40), detail: `${fmt(c.atendidos)} personas · ${c.jornadas} jornada(s)`,
  })), [r.data]);
  if (r.isLoading) return <Loading />;
  if (r.isError) return <ErrorNote>{(r.error as Error).message}</ErrorNote>;
  const d = r.data!;
  const alertas = (s.data?.anomalias ?? []).filter((a) => a.colonia !== 'Municipio');
  const jh = hoy.data?.es_hoy ? hoy.data.jornada : null;
  return (
    <>
      {jh && (
        <button type="button" onClick={() => navigate('/m/gov-health/jornada')}
          className="flex w-full flex-wrap items-center gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/5 px-4 py-3 text-left text-sm hover:bg-emerald-500/10">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" /></span>
          <span className="min-w-0 flex-1"><span className="font-semibold">Caravana de hoy en {jh.colonia}</span> · {fmt(jh.registrados)} persona(s) registradas con expediente · {fmt(jh.canalizaciones)} canalización(es)</span>
          <span className="inline-flex items-center gap-1 font-medium text-primary"><ClipboardPlus size={14} /> Abrir jornada</span>
        </button>
      )}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Personas atendidas · 30 días" value={fmt(d.personas_atendidas)} sub={`${d.jornadas} jornadas (realizadas y en curso)`} />
        <Stat label="Servicios otorgados" value={fmt(d.total_servicios)} sub={d.ocupacion_pct != null ? `${d.ocupacion_pct}% del cupo ocupado` : undefined} />
        <Stat label="Canalizaciones" value={fmt(d.canalizaciones.total)} sub="a IMSS-Bienestar, Hospital Central…" />
        <Stat label="Señales para revisar" value={fmt(alertas.length)}
          sub={`${alertas.filter((a) => a.nivel === 'alerta').length} alerta(s) · ${alertas.filter((a) => a.nivel === 'vigilancia').length} vigilancia`} />
      </div>
      <div className="grid gap-4 lg:grid-cols-5">
        <Card title="Atención por colonia" className="self-start lg:col-span-3" action={<OrigenBadge origen={d.origen} />}>
          <GovMap points={points} height={300} label="Mapa de personas atendidas por colonia" />
          <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
            {d.por_tipo.slice(0, 6).map((t) => (
              <li key={t.tipo} className="flex items-center justify-between rounded-xl bg-muted/50 px-3 py-2 text-sm">
                <span className="truncate text-foreground">{t.etiqueta}</span>
                <span className="font-semibold tabular-nums">{fmt(t.cantidad)}</span>
              </li>
            ))}
          </ul>
        </Card>
        <div className="flex flex-col gap-4 lg:col-span-2">
          <Card title="Próximas jornadas">
            {proximas.length === 0 ? <EmptyState icon={<CalendarDays size={18} />} title="Sin jornadas programadas" /> : (
              <ul className="flex flex-col gap-2">{proximas.map((x) => <RutaRow key={x.id} r={x} />)}</ul>
            )}
          </Card>
          <Card title={<span className="inline-flex items-center gap-1.5"><Sparkles size={15} className="text-primary" /> A dónde llevar la Ruta</span>}>
            {sug.isLoading ? <Loading /> : (
              <ol className="flex flex-col gap-2">
                {(sug.data?.sugerencias ?? []).map((g, i) => (
                  <li key={g.colonia} className="rounded-xl border border-border p-3">
                    <div className="flex items-center gap-2">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">{i + 1}</span>
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold">{g.colonia}</span>
                      <span className="text-xs tabular-nums text-muted-foreground">{g.puntaje} pts</span>
                    </div>
                    <ul className="mt-1.5 list-disc space-y-0.5 pl-8 text-xs text-muted-foreground">
                      {g.razones.slice(0, 3).map((x) => <li key={x}>{x}</li>)}
                    </ul>
                  </li>
                ))}
              </ol>
            )}
            {sug.data && <p className="mt-2 text-[11px] text-muted-foreground">{sug.data.aviso}</p>}
          </Card>
        </div>
      </div>
    </>
  );
}

function RutaRow({ r }: { r: Ruta }) {
  return (
    <li className="flex items-start gap-3 rounded-xl border border-border p-3">
      <div className="grid w-12 shrink-0 place-items-center rounded-lg bg-primary/10 py-1 text-center text-primary">
        <span className="text-[10px] font-semibold uppercase">{fechaCorta(r.fecha).split(' ')[0]}</span>
        <span className="text-lg font-bold leading-none">{Number(r.fecha.slice(8, 10))}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{r.colonia}</p>
        <p className="truncate text-xs text-muted-foreground"><MapPin size={11} className="mr-0.5 inline" />{r.sede}</p>
        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{r.servicios.map((s) => s.etiqueta).join(' · ')}</p>
      </div>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {r.atendidos != null ? `${fmt(r.atendidos)}/${fmt(r.cupo)}` : `cupo ${fmt(r.cupo)}`}
      </span>
    </li>
  );
}

// ── Ruta (calendario) ─────────────────────────────────────────

function RutaView() {
  const cal = useQuery({ queryKey: ['gov-health', 'calendario'], queryFn: () => govHealthApi.calendario() });
  const semanas = useMemo(() => {
    const m = new Map<string, Ruta[]>();
    (cal.data?.rutas ?? []).forEach((r) => {
      const [y, mo, d] = r.fecha.split('-').map(Number);
      const dt = new Date(y, mo - 1, d);
      dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
      const k = dt.toISOString().slice(0, 10);
      m.set(k, [...(m.get(k) ?? []), r]);
    });
    return [...m.entries()];
  }, [cal.data]);
  const points: MapPoint[] = (cal.data?.rutas ?? []).filter((r) => r.estado === 'programada')
    .map((r) => ({ lat: r.lat, lon: r.lon, label: r.colonia, color: '#4338CA', detail: `${fechaCorta(r.fecha)} · ${r.sede}` }));
  if (cal.isLoading) return <Loading />;
  if (cal.isError) return <ErrorNote>{(cal.error as Error).message}</ErrorNote>;
  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <Card title="Calendario de la Ruta de la Salud" className="lg:col-span-3" action={<OrigenBadge origen={cal.data!.origen} />}>
        <div className="flex flex-col gap-4">
          {semanas.map(([sem, rutas]) => (
            <section key={sem} aria-label={`Semana del ${fechaCorta(sem)}`}>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Semana del {fechaCorta(sem)}</h3>
              <ul className="grid gap-2 sm:grid-cols-3">
                {rutas.map((r) => (
                  <li key={r.id} className={`rounded-xl border p-2.5 text-xs ${r.estado === 'programada' ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
                    <p className="font-semibold text-foreground">{fechaCorta(r.fecha)}</p>
                    <p className="truncate text-foreground">{r.colonia}</p>
                    <p className="mt-0.5 text-muted-foreground">
                      {r.estado === 'realizada' ? `${fmt(r.atendidos)} atendidos` : r.estado === 'programada' ? `Programada · cupo ${r.cupo}` : r.estado}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </Card>
      <Card title="Próximas sedes" className="lg:col-span-2">
        <GovMap points={points} height={360} label="Mapa de las próximas jornadas" />
      </Card>
    </div>
  );
}

// ── Señales ───────────────────────────────────────────────────

function SenalesView() {
  const s = useQuery({ queryKey: ['gov-health', 'senales'], queryFn: () => govHealthApi.senales() });
  if (s.isLoading) return <Loading />;
  if (s.isError) return <ErrorNote>{(s.error as Error).message}</ErrorNote>;
  const d = s.data!;
  const anomalias = d.anomalias.filter((a) => a.colonia !== 'Municipio');
  const points: MapPoint[] = d.mapa.map((m) => ({
    lat: m.lat, lon: m.lon, label: m.colonia, color: NIVEL_COLOR[m.nivel] ?? NIVEL_COLOR[''],
    radius: m.nivel ? 13 : 7, detail: m.nivel ? `Nivel: ${m.nivel}` : `${m.total} casos esta semana`,
  }));
  return (
    <>
      <div className="grid gap-4 lg:grid-cols-5">
        <Card title={`Semana del ${fechaCorta(d.semana)}`} className="lg:col-span-2" action={<OrigenBadge origen={d.origen} />}>
          <GovMap points={points} height={320} label="Mapa de señales epidemiológicas por colonia" />
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-full" style={{ background: NIVEL_COLOR.alerta }} />Alerta</span>
            <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-full" style={{ background: NIVEL_COLOR.vigilancia }} />Vigilancia</span>
            <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-full" style={{ background: NIVEL_COLOR[''] }} />Sin anomalía</span>
          </div>
        </Card>
        <div className="flex flex-col gap-3 lg:col-span-3">
          {anomalias.length === 0 ? (
            <EmptyState icon={<HeartPulse size={18} />} title="Sin anomalías esta semana">Todas las series están dentro de lo esperado para estas fechas.</EmptyState>
          ) : anomalias.map((a) => <AnomaliaCard key={`${a.colonia}-${a.tipo}`} a={a} />)}
        </div>
      </div>
      <Card title="Cómo se calcula">
        <p className="text-sm text-foreground">{d.metodo}</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Se evaluaron {d.series_evaluadas} series (colonia × tipo). {d.descartadas_por_estacionalidad} habrían sonado
          comparando solo contra las semanas previas, pero son el alza normal de temporada: por eso se compara contra la misma época de otros años.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
          {d.tendencia.map((t) => (
            <div key={t.tipo} className="rounded-xl bg-muted/50 p-2.5">
              <p className="truncate text-[11px] font-medium text-muted-foreground">{t.etiqueta}</p>
              <p className="text-base font-semibold tabular-nums">{fmt(t.serie[t.serie.length - 1]?.conteo)}</p>
              <Sparkline data={t.serie.map((p) => p.conteo)} />
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{d.aviso}</p>
      </Card>
    </>
  );
}

function AnomaliaCard({ a }: { a: Anomalia }) {
  return (
    <article className={`orion-card rounded-[18px] border bg-card p-4 ${a.nivel === 'alerta' ? 'border-red-500/40' : 'border-amber-500/40'}`}>
      <header className="flex flex-wrap items-center gap-2">
        <NivelChip nivel={a.nivel} />
        <h3 className="min-w-0 flex-1 text-sm font-semibold">{a.etiqueta} · {a.colonia}</h3>
        <span className="text-xs tabular-nums text-muted-foreground">z = {a.z.toFixed(1)}</span>
      </header>
      <div className="mt-2 flex items-end gap-4">
        <div><p className="text-[11px] text-muted-foreground">Esta semana</p><p className="text-2xl font-semibold tabular-nums">{a.conteo}</p></div>
        <div><p className="text-[11px] text-muted-foreground">Esperado</p><p className="text-base tabular-nums">{a.esperado.toFixed(1)} <span className="text-xs text-muted-foreground">± {a.desviacion.toFixed(1)}</span></p></div>
        {a.dos_semanas_seguidas && <span className="mb-1 rounded-full bg-red-500/10 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:text-red-300">2 semanas seguidas</span>}
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{a.explicacion}</p>
      <p className="mt-1 text-[11px] text-muted-foreground">Base {a.metodo === 'estacional' ? 'estacional' : 'móvil'} · {a.puntos_base} semanas de referencia</p>
    </article>
  );
}

// ── Canalizaciones ────────────────────────────────────────────

function CanalizacionesView() {
  const [abierto, setAbierto] = useState<string | null>(null);
  const c = useQuery({ queryKey: ['gov-health', 'canalizaciones'], queryFn: () => govHealthApi.canalizaciones(), retry: false });
  if (c.isLoading) return <Loading />;
  if (c.isError) {
    const st = (c.error as any)?.status;
    return st === 403 ? (
      <EmptyState icon={<Users size={18} />} title="Necesitas permiso clínico">
        Las canalizaciones son por persona: solo las ve personal con acceso clínico (médico, enfermería, asistente clínico o administración). La coordinación ve los totales en Panorama.
      </EmptyState>
    ) : <ErrorNote>{(c.error as Error).message}</ErrorNote>;
  }
  const d = c.data!;
  const tone: Record<string, string> = {
    atendida: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300', no_acudio: 'bg-red-500/15 text-red-700 dark:text-red-300',
    agendada: 'bg-primary/10 text-primary', enviada: 'bg-muted text-foreground', cancelada: 'bg-muted text-muted-foreground',
  };
  return (
    <Card title={`Canalizaciones (${fmt(d.count)})`} action={<OrigenBadge origen={d.origen} />}>
      <ul className="flex flex-col divide-y divide-border">
        {d.items.slice(0, 40).map((x) => (
          <li key={x.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-sm">
            <span className="w-full font-mono text-[11px] text-muted-foreground sm:w-44">{x.folio}</span>
            <span className="min-w-0 flex-1"><span className="font-medium">{x.motivo_label}</span> → {x.destino_label}</span>
            <span className="text-xs text-muted-foreground">{x.colonia} · {fechaCorta(x.fecha)}</span>
            {x.prioridad === 'urgente' && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-300">Urgente</span>}
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone[x.estado] ?? ''}`}>{x.estado_label}</span>
            {x.patient_id && (
              <button type="button" onClick={() => setAbierto(x.patient_id)}
                className={`${osActionBtn} !h-7 !px-2 text-xs`}>Expediente</button>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-muted-foreground">Sin nombres ni datos de identidad: el expediente se abre en MedOS y la consulta queda en la bitácora.</p>
      {abierto && <ExpedienteDrawer patientId={abierto} onClose={() => setAbierto(null)} />}
    </Card>
  );
}
